import { useState, useEffect, useMemo } from "react";
import SortArrow from "../../../components/Atomos/Icons/SortArrow";
import AttendeeContactCard from "./AttendeeContactCard";
import { MemberEventAttendee } from "../../../interfaces/models/IMemberEventAttendee";
import { useNavigate, useParams } from "react-router-dom";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";
import { useEventDetailData } from "../../../hooks/Events/EventDetailDataContext";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Global from "../../../components/Atomos/Icons/Global";
import { NetworkingMapRoute } from "../../../Enums/EventEnums";
import { isProd } from "../../../Enums/LegacyRedirection";

export default function MemberAttendeesTab() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const event = useEventLoader(id);
  const { attendeesState, ensureAttendeesLoaded } = useEventDetailData();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<
    "country" | "firmName" | "lastName"
  >("country");

  useEffect(() => {
    if (!event?.id) {
      return;
    }

    void ensureAttendeesLoaded().catch(() => {
      // Error state is surfaced through attendeesState.
    });
  }, [ensureAttendeesLoaded, event?.id]);

  const attendees = useMemo<MemberEventAttendee[]>(() => {
    if (attendeesState.status !== "success") {
      return [];
    }
    return attendeesState.data;
  }, [attendeesState]);

  const sortedAttendees = useMemo(() => {
    if (!attendees.length) {
      return [];
    }

    const sorted = [...attendees].sort((a, b) => {
      let aValue: string = "";
      let bValue: string = "";

      switch (sortField) {
        case "country":
          aValue = (a.firm?.countryName || "").toLowerCase();
          bValue = (b.firm?.countryName || "").toLowerCase();
          break;
        case "firmName":
          aValue = (a.firm?.firmName || a.firm?.name || "").toLowerCase();
          bValue = (b.firm?.firmName || b.firm?.name || "").toLowerCase();
          break;
        case "lastName":
          aValue = (a.person.lastName || "").toLowerCase();
          bValue = (b.person.lastName || "").toLowerCase();
          break;
        default:
          return 0;
      }

      if (!aValue && !bValue) {
        return 0;
      }
      if (!aValue) {
        return 1;
      }
      if (!bValue) {
        return -1;
      }

      if (aValue < bValue) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [attendees, sortField, sortOrder]);

  if (!event) {
    return <div>Event not found</div>;
  }

  const isLoading =
    attendeesState.status === "idle" || attendeesState.status === "loading";
  const hasError = attendeesState.status === "error";

  return (
    <div className="bg-white text-left">
      <div className="border-b border-gray-400 pb-10">
        <div className="border-b border-gray-400">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-primary lg:text-[32px] font-medium lg:leading-8 md:text-[28px] md:leading-11 text-2xl leading-normal">
              Attendees
            </h2>
          </div>
          <div className="flex flex-wrap md:flex-nowrap flex-row gap-3 items-center justify-between py-5">
            <div className="flex flex-wrap md:flex-nowrap flex-row gap-3 items-center justify-start min-w-0">
            <div>
              <span className="text-primary not-italic font-semibold lg:leading-6 lg:text-xl md:text-lg text-base md:leading-5 leading-[18px]">
                Sort By
              </span>
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="2"
                height="20"
                viewBox="0 0 2 20"
                fill="none"
              >
                <path d="M1 0V20" stroke="#EBEBEB" />
              </svg>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => {
                  if (sortField === "country") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortField("country");
                    setSortOrder("asc");
                  }
                }}
              >
                <span className="text-gray-font not-italic font-semibold lg:leading-6 lg:text-xl md:text-lg text-base md:leading-5 leading-[18px]">
                  Country
                </span>
                <SortArrow
                  className={`flex w-6 h-6 items-center justify-center fill-gray-font ${
                    sortField === "country" &&
                    sortOrder === "desc" &&
                    "rotate-180"
                  }`}
                />
              </div>

              <span className="text-gray-font">•</span>

              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => {
                  if (sortField === "firmName") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortField("firmName");
                    setSortOrder("asc");
                  }
                }}
              >
                <span className="text-gray-font not-italic font-semibold lg:leading-6 lg:text-xl md:text-lg text-base md:leading-5 leading-[18px]">
                  Firm Name
                </span>
                <SortArrow
                  className={`flex w-6 h-6 items-center justify-center fill-gray-font ${
                    sortField === "firmName" &&
                    sortOrder === "desc" &&
                    "rotate-180"
                  }`}
                />
              </div>

              <span className="text-gray-font">•</span>

              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => {
                  if (sortField === "lastName") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortField("lastName");
                    setSortOrder("asc");
                  }
                }}
              >
                <span className="text-gray-font not-italic font-semibold lg:leading-6 lg:text-xl md:text-lg text-base md:leading-5 leading-[18px]">
                  Last Name
                </span>
                <SortArrow
                  className={`flex w-6 h-6 items-center justify-center fill-gray-font ${
                    sortField === "lastName" &&
                    sortOrder === "desc" &&
                    "rotate-180"
                  }`}
                />
              </div>
            </div>
            </div>
            {!isProd() && (
            <LegacyButton
              text="Connections"
              color="primary"
              style="filled"
              size="small"
              content="textIcon"
              textIconLayout="cluster"
              iconOrder="textFirst"
              icon={
                <Global className="h-5 w-5 shrink-0 text-white" />
              }
              onClick={() => navigate(`/events/${id}/${NetworkingMapRoute}`)}
              className="!w-auto shrink-0"
            />
            )}
          </div>
        </div>

        <div className="mt-6">
          {isLoading ? null : hasError ? (
            <div className="text-center py-8 text-red-600">
              Unable to load attendees. Please try again later.
            </div>
          ) : sortedAttendees.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No attendees registered yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sortedAttendees.map((attendee, index) => (
                <AttendeeContactCard
                  key={attendee.registration.id || index}
                  attendee={attendee}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
